import * as React from "react";
import {
  Box,
  Checkbox,
  CheckboxGroup,
  Column,
  Columns,
  Divider,
  Spinner,
  Stack,
  Text,
} from "@origin-digital/ods-core";
import {
  Field,
  FieldInputProps,
  FieldProps,
  FormikErrors,
  FormikProps,
  useFormikContext,
} from "formik";
import { AccountPayer, EnergyType, FormValues } from "../types";
import FormContext, { GasService } from "src/context/FormContext";
import { useMemo } from "react";
import styled from "styled-components";
import { apiClient } from "src/api/apiService";
import { mapInternetStatusLookupRequest } from "src/api/apiHelper";
import ServiceTypeStatusAlert, {
  InternetRequestStatusEnum,
} from "../ServiceTypeStatusAlert";

interface IServiceTypesWrapperProps {
  error:
    | string
    | FormikErrors<any>
    | FormikErrors<any>[]
    | string[]
    | undefined;
  isEditing: boolean;
  isDuplicate?: boolean;
}
interface IServiceTypesProps {
  error:
    | string
    | FormikErrors<any>
    | FormikErrors<any>[]
    | string[]
    | undefined;
  isEditing: boolean;
  handleInternetStatusLookup: () => Promise<void>;
  loading: boolean;
  isDuplicate?: boolean;
}

const ServiceTypes = (props: IServiceTypesProps) => {
  const { error, isEditing, isDuplicate, handleInternetStatusLookup, loading } =
    props;
  const { values, setErrors, setFieldValue } = useFormikContext<FormValues>();
  const {
    formState: { electricityService, gasServices, internetService },
  } = React.useContext(FormContext);
  const isAddressAdded =
    !!values.premiseAddress.state && !!values.premiseAddress.postcode;

  const gasServicesValues: GasService[] = gasServices
    ? Object.values(gasServices)
    : [];

  const isElectricityDisabled = Boolean(
    (electricityService?.draftId && isEditing) ||
      electricityService?.channel === "CLAIM" ||
      electricityService?.isReadOnly
  );

  const isInternetDisabled = Boolean(
    !isAddressAdded ||
      loading ||
      (!!values.internetRequestStatus &&
        values.internetRequestStatus !== InternetRequestStatusEnum.CAN_RETAIL &&
        values.internetRequestStatus !==
          InternetRequestStatusEnum.PRE_INSTALL_NBN &&
        values.internetRequestStatus !==
          InternetRequestStatusEnum.ADDRESS_NOT_FOUND) ||
      (isEditing && internetService?.draftId) ||
      internetService?.isReadOnly ||
      isDuplicate
  );

  const accountPayers = useMemo(
    () => [
      values.electricityAccountPayer,
      values.gasLine?.accountPayer,
      values.gasMeter?.accountPayer,
      values.both?.accountPayer,
      values.internetAccountPayer,
    ],
    [
      values.electricityAccountPayer,
      values.gasLine?.accountPayer,
      values.gasMeter?.accountPayer,
      values.both?.accountPayer,
      values.internetAccountPayer,
    ]
  );

  const isGasDisabled = Boolean(
    isEditing &&
      gasServicesValues.length > 0 &&
      gasServicesValues.some((gasService: GasService) => {
        return gasService.draftId || gasService.isReadOnly;
      })
  );

  React.useEffect(() => {
    if (
      !values.selectedConnections.includes("Internet") &&
      values.isDisablePremiseButton
    ) {
      setErrors({ selectedConnections: "" });
    }
  }, [values.selectedConnections]);

  const handleInternetCheckboxChange = (
    e: React.ChangeEvent<HTMLInputElement>
  ) => {
    const { checked } = e.target;
    if (checked) {
      handleInternetStatusLookup();
    } else {
      const updatedConnections = values.selectedConnections.filter(
        (connection) => connection !== "Internet"
      );
      setFieldValue("selectedConnections", updatedConnections);
      setFieldValue("internetRequestStatus", null);
    }
  };

  const showHelperText = () => {
    let helperText = "";
    helperText = isAddressAdded ? "" : "Please enter an address first.";

    if (
      values.internetRequestStatus === "PRE_INSTALL_REQD_OPTICOMM" ||
      values.internetRequestStatus === "NOT_AVAILABLE"
    ) {
      helperText = "Not currently available.";
    }

    if (values.internetRequestStatus === "ADDRESS_NOT_FOUND") {
      helperText =
        "Selecting internet will send the account holder an email to compare our internet plans. There's no obligation for them to sign up with Origin.";
    }

    if (values.internetRequestStatus === "CAN_RETAIL") {
      helperText =
        "As pre-installation is already completed, selecting internet will send the account holder an email to compare our internet plans. There's no obligation for them to sign up with Origin.";
    }

    if (
      !values.selectedConnections.includes("Internet") &&
      isAddressAdded &&
      !values.internetRequestStatus
    ) {
      helperText = "";
    }

    return helperText;
  };
  const showReadOnly =
    isElectricityDisabled && isGasDisabled && isInternetDisabled && isEditing;

  return showReadOnly ? (
    <ReadOnlyServiceTypes selectedConnections={values.selectedConnections} />
  ) : (
    <CheckboxGroup
      id="connection-type"
      label="What’s being connected?"
      //@ts-expect-error: linting does not allow for
      error={error}
      helperText={error as string}
      //@ts-expect-error: auto blur to make TextLint in help text clickable
      onClick={(e) => e.target.blur()}
    >
      <Box
        display="flex"
        flexWrap="wrap"
        flexDirection={{
          xs: "column",
          sm: "column",
        }}
      >
        <Field type="checkbox" name="selectedConnections" value="Electricity">
          {({ field }: { field: FormikProps<FieldProps> }) => (
            <Checkbox
              {...field}
              id="electricity"
              label="Electricity"
              value="Electricity"
              disabled={isElectricityDisabled || loading}
            />
          )}
        </Field>
        <Field type="checkbox" name="selectedConnections" value="Gas">
          {({ field }: { field: FieldInputProps<boolean> }) => (
            <Checkbox
              {...field}
              id="natural-gas"
              label="Natural Gas"
              value="Gas"
              onChange={(e) => {
                field.onChange(e);
                //special case for ACT as no gas options are displayed for user to select
                if (
                  accountPayers.includes(AccountPayer.SOMEONE_ELSE) &&
                  values.premiseAddress.state === "ACT"
                ) {
                  values.gasOptions.forEach((option) => {
                    setFieldValue(
                      `${option}.accountPayer`,
                      AccountPayer.SOMEONE_ELSE
                    );
                  });
                }
              }}
              disabled={isGasDisabled || loading}
            />
          )}
        </Field>
        <Field type="checkbox" name="selectedConnections" value="Internet">
          {({ field }: { field: FieldInputProps<boolean> }) => (
            <Checkbox
              {...field}
              id="internet"
              label="Internet"
              value="Internet"
              helperText={loading ? "" : showHelperText()}
              disabled={isInternetDisabled}
              onChange={(e) => handleInternetCheckboxChange(e)}
            />
          )}
        </Field>
      </Box>
    </CheckboxGroup>
  );
};

export const ServiceTypesWrapper = (props: IServiceTypesWrapperProps) => {
  const { values, setFieldValue } = useFormikContext<FormValues>();
  const { setDisplayingSnackbar, setFormState } = React.useContext(FormContext);
  const [loading, setLoading] = React.useState<boolean>(false);

  const StyledSpinner = styled(Spinner)({
    position: "absolute",
    top: "50%",
    left: "50%",
    transform: "translate(-50%,-50%)",
    zIndex: 200,
  });

  const handleInternetStatusLookup = async () => {
    try {
      setLoading(true);
      const { data } = await apiClient.post(
        `/internet/status-look-up`,
        mapInternetStatusLookupRequest(values)
      );
      const status = data?.outcome?.status;
      setFieldValue("internetRequestStatus", status);
      setFieldValue("locId", data.premise.loc_id || "");
      setFieldValue("internetAnexAddress", data.anex_address || "");

      // Set in formState instead of Formik values
      setFormState((prevState) => ({
        ...prevState,
        internetStatusLookup: data,
      }));

      if (
        status === InternetRequestStatusEnum.CAN_RETAIL ||
        status === InternetRequestStatusEnum.PRE_INSTALL_NBN ||
        status === InternetRequestStatusEnum.ADDRESS_NOT_FOUND
      ) {
        if (!values.selectedConnections.includes("Internet" as EnergyType)) {
          setFieldValue("selectedConnections", [
            ...values.selectedConnections,
            "Internet",
          ]);
        }
      } else {
        const updatedConnections = values.selectedConnections.filter(
          (connection) => connection !== "Internet"
        );
        setFieldValue("selectedConnections", updatedConnections);
      }
    } catch (e) {
      setDisplayingSnackbar({
        message: `Failed to add Internet to request`,
        tone: "critical",
      });
      console.error("Failed to save account:", e);
    } finally {
      setLoading(false);
    }
  };

  React.useEffect(() => {
    if (
      !props.isEditing &&
      (values.selectedConnections.includes("Internet") ||
        values.internetRequestStatus)
    ) {
      handleInternetStatusLookup();
    }
  }, [values.premiseAddress]);
  return (
    <>
      {loading ? (
        <Box display="inline-flex" style={{ position: "relative" }}>
          <StyledSpinner size="small" />
          <ServiceTypes
            {...props}
            handleInternetStatusLookup={handleInternetStatusLookup}
            loading={loading}
          />
        </Box>
      ) : (
        <Stack>
          <ServiceTypes
            {...props}
            handleInternetStatusLookup={handleInternetStatusLookup}
            loading={loading}
          />
          {values.internetRequestStatus && (
            <ServiceTypeStatusAlert status={values.internetRequestStatus} />
          )}
        </Stack>
      )}
    </>
  );
};
interface ReadOnlyServiceTypesProps {
  selectedConnections: string[];
}

export const ReadOnlyServiceTypes: React.FC<ReadOnlyServiceTypesProps> = ({
  selectedConnections,
}) => {
  return (
    <Columns
      collapseBelow="md"
      space={{
        xs: "xsmall",
        md: "xxlarge",
      }}
      alignY="stretch"
    >
      <Column width="2/3">
        {/* What’s being connected? */}
        <Stack space="xsmall">
          <Stack space="xxsmall">
            <Text variant="body-small" tone="neutralLight">
              What’s being connected?
            </Text>
            <Text data-role="value" data-id="selectedConnectionsReadOnly">
              {selectedConnections.join(", ")}
            </Text>
          </Stack>
          <Divider />
        </Stack>
        {/* End What’s being connected? */}
      </Column>
      {/* HelperText */}
      <Column width="1/3" />
      {/* End HelperText */}
    </Columns>
  );
};

export default ServiceTypes;
